import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// Actions
import { getRegionNames } from "../../../actions/regions/RegionActions";
import { getMapViewExploreSites } from "../../../actions/map-view/media-sites/ExploreSitesActions";
import { openSingleCitySelectionForm } from "../../../actions/SingleCitySelectionFormActions";

// Constants and Utils
import {
  DefaultPagination,
  FormDataTargets,
  LocalStorageKeys,
  OrgSettingsIndexes,
  PageSize,
} from "../../../constants/GeneralConstants";
import { getItem } from "../../../utils/localstorage";
import { toLocaleString } from "../../../common-utils/string-utils/StringUtils";
import {
  getMediasUniqueStretchCount,
  getUserAssociatedMediasMap,
} from "./InventoryMediaUtils";

// Component
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import TableHeaders from "../../../components/table/TableHeaders";
import SingleCitySelectionForm from "../../../mavin/components/single-city-selection-form/SingleCitySelectionForm";
import OrgSettingsSidebar from "../../../pages/org/OrgSettingsSidebar";
import SearchInput from "../../../components/search-input/SearchInput";
import { LoadingData } from "../../../pages/org/OrgUtils";

// Pages and Sections
import InventoryUserMediasDetailsForm from "./InventoryUserMediasDetailsForm";

// Page-constants
const tableHeaders = [
  {
    title: {
      displayName: "User ID",
      className: "col-3 text-center",
    },
  },
  {
    title: {
      displayName: "Media Count",
      className: "col-3 text-center",
    },
  },
  {
    title: {
      displayName: "Stretch Count",
      className: "col-3 text-center",
    },
  },
  {
    title: {
      displayName: "Action",
      className: "col-3 text-center",
    },
  },
];

// Page Components
// ----------------------------------------------------------------------------
function MediaUserDetailsTable({
  searchedUserAssociatedMediasMap = {},
  setOpenUserMediasModal,
  setSelectedUserId,
}) {
  // loading
  const mediaListLoading = useSelector(
    (state) => state.mapViewExploreSites.loading
  );

  // Loading
  if (mediaListLoading) {
    return <LoadingData className="p-1" dataType="Medias" />;
  }

  function handleOpenUserMediaForm(userId) {
    setSelectedUserId(userId);
    setOpenUserMediasModal(true);
  }

  return (
    <div className="table-responsive">
      <table className="table">
        {/* Table Header */}
        <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />

        {/* Table Row */}
        <tbody>
          {Object.keys(searchedUserAssociatedMediasMap).map((userId) => {
            const medias = searchedUserAssociatedMediasMap[userId] || [];

            const mediaCount = medias ? medias.length : 0;
            const formattedMediaCount =
              mediaCount === 0 ? 0 : toLocaleString(mediaCount);

            const { stretchCount } = getMediasUniqueStretchCount(medias);
            const formattedStretchCount =
              stretchCount === 0 ? 0 : toLocaleString(stretchCount);

            return (
              <tr key={userId}>
                <td className="text-center">{userId}</td>
                <td className="text-center">{formattedMediaCount}</td>
                <td className="text-center">{formattedStretchCount}</td>
                <td className="text-center">
                  <button
                    className="btn btn-link shadow-none p-0"
                    data-toggle="modal"
                    data-target={`#${FormDataTargets.inventoryUserMediasDetailsForm}`}
                    onClick={() => handleOpenUserMediaForm(userId)}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CitySelectSection({ regionsName = [], selectedCityId }) {
  const dispatch = useDispatch();

  const selectedRegion = regionsName.find((item) => item.id === selectedCityId);
  const { label: regionName } = selectedRegion || {};

  return (
    <>
      <div className="d-flex align-items-center">
        <h5 className="mb-0 mr-2">Selected City : </h5>
        <button
          type="button"
          className="btn border rounded-lg shadow-none px-3 dropdown-toggle"
          data-toggle="modal"
          data-target={`#${FormDataTargets.singleCitySelectionForm}`}
          onClick={() => dispatch(openSingleCitySelectionForm())}
        >
          {regionName ? regionName : "Select"}
        </button>
      </div>
      <hr className="my-3" />
    </>
  );
}

/**
 * InventoryMediaListPage
 */
export default function InventoryMediaListPage() {
  const dispatch = useDispatch();

  // State
  const [selectedCityId, setSelectedCityId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedUserAssociatedMediasMap, setSearchedUserAssociatedMediasMap] =
    useState({});
  const [openUserMediasModal, setOpenUserMediasModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");

  // regions name list
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionNamesLoading = useSelector((state) => state.region.namesLoading);

  // single City-Selection-form
  const openSingleCitySelectionForm = useSelector(
    (state) => state.singleCitySelectionModal.openModal
  );

  // inventory media list for org
  const mediaList = useSelector(
    (state) => state.mapViewExploreSites.originalExploreSites
  );

  // getting user associated medias map
  const userAssociatedMediasMap = getUserAssociatedMediasMap(mediaList);

  useEffect(() => {
    dispatch(getRegionNames());
  }, [dispatch]);

  // initial update state
  useEffect(() => {
    setSearchedUserAssociatedMediasMap(userAssociatedMediasMap);
  }, [Object.keys(userAssociatedMediasMap).length]);

  // loggedIn user data
  const user = JSON.parse(getItem(LocalStorageKeys.USER));
  const { orgId } = user || {};

  // Pagination
  const pageNumber = DefaultPagination.pageNumber;
  const pageSize = PageSize.inventoryMediaList;

  // Functions
  function onSubmitCitySelectionForm(cityId) {
    setSelectedCityId(cityId);
    dispatch(getMapViewExploreSites(cityId, true, orgId, pageNumber, pageSize));
  }

  // Functions
  function onClickSearch() {
    if (!searchText) {
      setSearchedUserAssociatedMediasMap(userAssociatedMediasMap);
      return;
    }

    // Convert object to entries, filter by key string match
    const filteredEntries = Object.entries(userAssociatedMediasMap).filter(
      ([key]) => key.includes(searchText)
    );

    // Convert back to object
    const filteredMap = Object.fromEntries(filteredEntries);
    return setSearchedUserAssociatedMediasMap(filteredMap);
  }

  return (
    <>
      <OrgSettingsSidebar activeIdx={OrgSettingsIndexes.mediaInventory} />

      <div className="content-wrapper">
        <PageHeader title={"Org :: Media Inventory"} shadow={true} />

        {/** Page Content */}
        <div className="page-content">
          {/* region data loading */}
          {regionNamesLoading && <LoadingData className="p-1" />}

          {/* Content */}
          {!regionNamesLoading && (
            <>
              <CitySelectSection
                regionsName={regionsName}
                selectedCityId={selectedCityId}
              />

              {/** Table Header actions */}
              {selectedCityId && (
                <>
                  <div className="table-header">
                    <SearchInput
                      placeholder={"Search User ID"}
                      onClickSearch={onClickSearch}
                      onTextChange={({ target }) => setSearchText(target.value)}
                    />
                  </div>

                  {/* Table */}
                  <MediaUserDetailsTable
                    searchedUserAssociatedMediasMap={
                      searchedUserAssociatedMediasMap
                    }
                    setOpenUserMediasModal={setOpenUserMediasModal}
                    setSelectedUserId={setSelectedUserId}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* MODALS */}
        {openSingleCitySelectionForm && (
          <SingleCitySelectionForm
            regionsName={regionsName}
            onSubmit={onSubmitCitySelectionForm}
            selectedCityId={selectedCityId}
          />
        )}

        {openUserMediasModal && (
          <InventoryUserMediasDetailsForm
            userAssociatedMediasMap={userAssociatedMediasMap}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            setOpenUserMediasModal={setOpenUserMediasModal}
          />
        )}
      </div>
    </>
  );
}
